import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACCOUNT_DEACTIVATED_EVENT } from "../../utils/accountDeactivated";

/**
 * App-wide blocking modal shown when the current user's account is deactivated
 * by an admin. It listens for the global `account-deactivated` event (dispatched
 * by handleAccountDeactivated on a 403 ACCOUNT_DEACTIVATED response) and, on the
 * button click, clears the session and navigates to the login page.
 */
const AccountDeactivatedModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onDeactivated = () => setOpen(true);
    window.addEventListener(ACCOUNT_DEACTIVATED_EVENT, onDeactivated);
    return () => window.removeEventListener(ACCOUNT_DEACTIVATED_EVENT, onDeactivated);
  }, []);

  const goToLogin = () => {
    try {
      sessionStorage.clear();
    } catch (e) {
      // ignore storage errors
    }
    window.location.replace("/");
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true">
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EC003F"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12" y2="12.5" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h2 style={styles.title}>
          {t("accountDeactivated.title", "Account Deactivated")}
        </h2>

        <p style={styles.message}>
          {t(
            "accountDeactivated.message",
            "Your account has been deactivated by the administrator. Please contact support for assistance.",
          )}
        </p>

        <button type="button" style={styles.button} onClick={goToLogin}>
          {t("accountDeactivated.button", "Go to Login")}
        </button>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100000,
    padding: "16px",
  },
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "32px 28px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
  },
  iconWrap: {
    width: "68px",
    height: "68px",
    borderRadius: "50%",
    background: "#FFF1F2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  title: {
    margin: "0 0 12px",
    fontSize: "20px",
    fontWeight: 700,
    color: "#1f2937",
  },
  message: {
    margin: "0 0 28px",
    fontSize: "15px",
    lineHeight: 1.5,
    color: "#6b7280",
  },
  button: {
    width: "100%",
    padding: "12px 16px",
    border: "none",
    borderRadius: "12px",
    background: "#0DD0D0",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default AccountDeactivatedModal;
